import React, { useState } from 'react';
import QRCodeModal from './QRCodeModal';
import StatsModal from './StatsModal';
import { useResponsive } from '../../hooks/useResponsive';
import { User, Hash, Link as LinkIcon, Copy, QrCode, BarChart3, Star, TrendingUp, Calendar } from 'lucide-react';

function ManagerCard({ manager }) {
  const [showQRModal, setShowQRModal] = useState(false);
  const [showStatsModal, setShowStatsModal] = useState(false);
  const { isMobile } = useResponsive();
  const reviewLink = manager.review_link || manager.reviewLink || '';

  const copyLink = () => {
    navigator.clipboard.writeText(reviewLink);
    // Using native browser notification instead of alert
    const notification = document.createElement('div');
    notification.textContent = '✓ Link copied!';
    notification.style.cssText = `
      position: fixed;
      top: ${isMobile ? '16px' : '24px'};
      right: ${isMobile ? '16px' : '24px'};
      left: ${isMobile ? '16px' : 'auto'};
      background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
      color: white;
      padding: ${isMobile ? '14px 20px' : '16px 24px'};
      border-radius: 12px;
      box-shadow: 0 8px 24px rgba(0,0,0,0.2);
      z-index: 99999;
      font-weight: 600;
      font-size: ${isMobile ? '14px' : '15px'};
      animation: slideIn 0.3s ease-out;
    `;
    document.body.appendChild(notification);
    setTimeout(() => {
      notification.style.animation = 'slideOut 0.3s ease-out';
      setTimeout(() => notification.remove(), 300);
    }, 2000);
  };

  return (
    <>
      <div className="card" style={{ 
        transition: 'all 0.3s', 
        cursor: 'default',
        padding: isMobile ? '20px' : '28px'
      }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: isMobile ? '10px' : '12px', 
          marginBottom: '16px',
          flexWrap: 'wrap'
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            padding: isMobile ? '8px' : '10px',
            borderRadius: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <User size={isMobile ? 18 : 20} color="white" />
          </div>
          <h3 style={{ 
            margin: 0, 
            fontSize: isMobile ? '18px' : '20px', 
            fontWeight: '600' 
          }}>{manager.manager_name}</h3>
        </div>
        
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '8px',
          padding: isMobile ? '8px' : '10px',
          background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
          borderRadius: '8px',
          marginBottom: '16px'
        }}>
          <Hash size={isMobile ? 14 : 16} color="#667eea" />
          <p style={{ 
            color: '#495057', 
            fontSize: isMobile ? '13px' : '14px', 
            margin: 0, 
            fontWeight: '600' 
          }}>
            Code: {manager.unique_code}
          </p>
        </div>

        <div style={{ marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
            <LinkIcon size={isMobile ? 13 : 14} color="#7f8c8d" />
            <p style={{ 
              fontSize: isMobile ? '12px' : '13px', 
              color: '#7f8c8d', 
              margin: 0, 
              fontWeight: '600' 
            }}>Review Link:</p>
          </div>
          <div style={{ 
            display: 'flex', 
            gap: '8px',
            flexDirection: isMobile ? 'column' : 'row'
          }}>
            <input
              type="text"
              value={reviewLink}
              readOnly
              style={{ 
                fontSize: isMobile ? '12px' : '13px', 
                padding: isMobile ? '8px' : '10px', 
                marginBottom: '0', 
                flex: 1 
              }}
            />
            <button 
              className="btn" 
              onClick={copyLink} 
              style={{ 
                padding: isMobile ? '8px 14px' : '10px 16px',
                background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                fontWeight: '600',
                border: 'none',
                fontSize: isMobile ? '14px' : '15px'
              }}
            >
              <Copy size={isMobile ? 14 : 16} />
              <span>Copy</span>
            </button>
          </div>
        </div>

        <div style={{ 
          display: 'flex', 
          gap: '8px', 
          flexWrap: 'wrap', 
          marginBottom: '16px',
          flexDirection: isMobile ? 'column' : 'row'
        }}>
          <button 
            className="btn" 
            style={{ 
              flex: 1, 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              fontWeight: '600',
              border: 'none',
              padding: isMobile ? '10px' : '12px',
              fontSize: isMobile ? '14px' : '15px'
            }}
            onClick={() => setShowQRModal(true)}
          >
            <QrCode size={isMobile ? 16 : 18} />
            <span>View QR</span>
          </button>
          <button 
            className="btn" 
            style={{ 
              flex: 1, 
              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', 
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              fontWeight: '600',
              border: 'none',
              padding: isMobile ? '10px' : '12px',
              fontSize: isMobile ? '14px' : '15px'
            }}
            onClick={() => setShowStatsModal(true)}
          >
            <BarChart3 size={isMobile ? 16 : 18} />
            <span>View Stats</span>
          </button>
        </div>

        {/* Stats Summary */}
        <div style={{ 
          marginTop: '16px',
          padding: isMobile ? '12px' : '16px',
          background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
          borderRadius: '12px',
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: isMobile ? '8px' : '12px',
          fontSize: '14px',
          border: '1px solid #e8ecf1'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '6px' }}>
              <Star size={isMobile ? 14 : 16} color="#667eea" />
            </div>
            <div style={{ 
              fontWeight: 'bold', 
              color: '#2c3e50', 
              fontSize: isMobile ? '18px' : '20px' 
            }}>{manager.total_reviews || 0}</div>
            <div style={{ 
              color: '#7f8c8d', 
              fontSize: isMobile ? '11px' : '12px', 
              fontWeight: '600' 
            }}>Total</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '6px' }}>
              <TrendingUp size={isMobile ? 14 : 16} color="#11998e" />
            </div>
            <div style={{ 
              fontWeight: 'bold', 
              color: '#2c3e50', 
              fontSize: isMobile ? '18px' : '20px' 
            }}>{manager.reviews_this_month || 0}</div>
            <div style={{ 
              color: '#7f8c8d', 
              fontSize: isMobile ? '11px' : '12px', 
              fontWeight: '600' 
            }}>Month</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '6px' }}>
              <Calendar size={isMobile ? 14 : 16} color="#f093fb" />
            </div>
            <div style={{ 
              fontWeight: 'bold', 
              color: '#2c3e50', 
              fontSize: isMobile ? '18px' : '20px' 
            }}>{manager.reviews_this_week || 0}</div>
            <div style={{ 
              color: '#7f8c8d', 
              fontSize: isMobile ? '11px' : '12px', 
              fontWeight: '600' 
            }}>Week</div>
          </div>
        </div>
      </div>

      {/* Modals */}
      {showQRModal && (
        <QRCodeModal 
          manager={manager} 
          onClose={() => setShowQRModal(false)} 
        />
      )}

      {showStatsModal && (
        <StatsModal 
          manager={manager} 
          onClose={() => setShowStatsModal(false)} 
        />
      )}
    </>
  );
}

export default ManagerCard;
